import React, { useState, useMemo } from "react";
import { X, Check, AlertCircle, Sparkles } from "lucide-react";

const IMPORT_RESERVED_KEYS = new Set([
  "clientcode",
  "infinitycode",
  "date",
  "allowances",
  "deductions",
  "notes",
  "description",
]);

const normalizeColumnKey = (rawKey) =>
  String(rawKey || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const deriveStoredType = (rawType, fallbackType = "") => {
  const value = String(rawType || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .trim();

  const withoutSuffix = value
    .replace(/\s*allowances?$/i, "")
    .replace(/\s*deductions?$/i, "")
    .replace(/\s*reductions?$/i, "")
    .trim();

  const finalValue = withoutSuffix || fallbackType;
  return finalValue.replace(/\s+/g, "_");
};

const ensureKindSuffix = (label, kind) => {
  const trimmed = String(label || "").trim();
  const normalizedKey = normalizeColumnKey(trimmed);

  if (kind === "allowance") {
    if (/_allowances?$/.test(normalizedKey) || /\ballowances?\b/i.test(trimmed)) {
      return trimmed;
    }
    return `${trimmed} Allowance`;
  }
  if (kind === "deduction") {
    if (
      /_(deductions?|reductions?)$/.test(normalizedKey) ||
      /\b(deductions?|reductions?)\b/i.test(trimmed)
    ) {
      return trimmed;
    }
    return `${trimmed} Deduction`;
  }
  return trimmed;
};

export const AddCustomColumnPanel = ({
  isOpen,
  onClose,
  onAddColumn,
  catalogTypes = [],
  existingCustomColumns = [],
  currentTheme,
  theme,
  isRTL,
  t,
}) => {
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState("allowance"); // "allowance" | "deduction"
  const [selectedVariants, setSelectedVariants] = useState(["net", "gross"]);

  // Catalog keys set for collision detection (V4)
  const catalogKeysSet = useMemo(() => {
    const s = new Set();
    catalogTypes.forEach((c) => {
      s.add(c.key);
      if (c.derivedType) s.add(c.derivedType);
      s.add(normalizeColumnKey(c.label));
    });
    return s;
  }, [catalogTypes]);

  // Existing custom keys in current selection (V5)
  const existingCustomKeysSet = useMemo(() => {
    const s = new Set();
    existingCustomColumns.forEach((c) => {
      s.add(c.key);
      if (c.derivedType) s.add(c.derivedType);
    });
    return s;
  }, [existingCustomColumns]);

  // Real-time live validation
  const validation = useMemo(() => {
    const trimmed = label.trim();

    // V1: Non-empty
    if (!trimmed) {
      return { valid: false, error: t("customColumnNameRequired") };
    }

    // V2: <= 60 chars
    if (trimmed.length > 60) {
      return { valid: false, error: t("customColumnNameTooLong") };
    }

    // V3: Alphanumeric check
    const normalized = normalizeColumnKey(trimmed);
    if (!normalized) {
      return { valid: false, error: t("customColumnNameInvalid") };
    }

    const fullLabel = ensureKindSuffix(trimmed, kind);
    const fullKey = normalizeColumnKey(fullLabel);
    const derivedType = deriveStoredType(fullLabel, kind);

    // V8: Derived type non-empty after stripping
    if (!derivedType || derivedType === "allowance" || derivedType === "deduction") {
      return { valid: false, error: t(kind === "allowance" ? "customColumnNeedWordAllowance" : "customColumnNeedWordDeduction") };
    }

    // V6: Reserved keys
    const cleanDerived = derivedType.replace(/_/g, "").toLowerCase();
    if (IMPORT_RESERVED_KEYS.has(cleanDerived) || IMPORT_RESERVED_KEYS.has(normalized.replace(/_/g, ""))) {
      return { valid: false, error: t("customColumnReserved") };
    }

    // V4: Catalog collision
    if (catalogKeysSet.has(fullKey) || catalogKeysSet.has(normalized) || catalogKeysSet.has(derivedType)) {
      return { valid: false, error: t("customColumnExists").replace("{name}", trimmed) };
    }

    // V5: Selection collision
    if (existingCustomKeysSet.has(fullKey) || existingCustomKeysSet.has(derivedType)) {
      return { valid: false, error: t("customColumnDuplicate") };
    }

    // V7: Variant check
    if (selectedVariants.length === 0) {
      return { valid: false, error: t("customColumnPickVariant") };
    }

    // Headers generated
    const headers = [];
    if (selectedVariants.includes("net")) {
      headers.push({ header: `${fullLabel} (Net)`, key: `${fullKey}_net` });
    }
    if (selectedVariants.includes("gross")) {
      headers.push({ header: `${fullLabel} (Gross)`, key: `${fullKey}_gross` });
    }
    if (selectedVariants.includes("days_net")) {
      headers.push({ header: `${fullLabel} Days (Net)`, key: `${fullKey}_days_net` });
    }
    if (selectedVariants.includes("days_gross")) {
      headers.push({ header: `${fullLabel} Days (Gross)`, key: `${fullKey}_days_gross` });
    }

    return {
      valid: true,
      fullLabel,
      fullKey,
      derivedType,
      headers,
    };
  }, [label, kind, selectedVariants, catalogKeysSet, existingCustomKeysSet, t]);

  const handleToggleVariant = (vKey) => {
    setSelectedVariants((prev) =>
      prev.includes(vKey) ? prev.filter((v) => v !== vKey) : [...prev, vKey]
    );
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validation.valid) return;

    onAddColumn({
      label: validation.fullLabel,
      kind,
      variants: selectedVariants,
      key: validation.fullKey,
      derivedType: validation.derivedType,
    });

    setLabel("");
    setSelectedVariants(["net", "gross"]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        insetInlineEnd: 0,
        width: "min(440px, 100%)",
        background: theme === "dark" ? "#1e293b" : "#ffffff",
        boxShadow: `${isRTL ? 8 : -8}px 0 24px rgba(0,0,0,0.25)`,
        zIndex: 20,
        borderInlineStart: `1px solid ${currentTheme.colors.border}`,
        display: "flex",
        flexDirection: "column",
        animation: "slideIn 0.2s ease-out",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 20px",
          borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Sparkles size={16} color={currentTheme.colors.primary} />
          <div style={{ fontSize: "0.95rem", fontWeight: 700, color: currentTheme.colors.textPrimary }}>
            {t("addColumn") || "Add Custom Column"}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: currentTheme.colors.textMuted,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Form Body */}
      <form
        onSubmit={handleSubmit}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "18px",
        }}
      >
        {/* Column Label */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "0.8rem", fontWeight: 600, color: currentTheme.colors.textSecondary }}>
            {t("customColumnLabel") || "Column name"}
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("customColumnPlaceholder")}
            maxLength={60}
            autoFocus
            style={{
              padding: "9px 12px",
              borderRadius: "8px",
              border: `1px solid ${currentTheme.colors.border}`,
              background: currentTheme.colors.inputBackground,
              color: currentTheme.colors.textPrimary,
              fontSize: "0.85rem",
              outline: "none",
            }}
          />
        </div>

        {/* Kind Toggle (Allowance vs Deduction) */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "0.8rem", fontWeight: 600, color: currentTheme.colors.textSecondary }}>
            {t("customColumnKind") || "Type"}
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => setKind("allowance")}
              style={{
                flex: 1,
                padding: "8px",
                borderRadius: "8px",
                border: kind === "allowance" ? "1.5px solid #006100" : `1px solid ${currentTheme.colors.border}`,
                background: kind === "allowance" ? (theme === "dark" ? "rgba(0,97,0,0.25)" : "#C6EFCE") : "transparent",
                color: kind === "allowance" ? (theme === "dark" ? "#86efac" : "#006100") : currentTheme.colors.textSecondary,
                fontWeight: kind === "allowance" ? 700 : 400,
                fontSize: "0.82rem",
                cursor: "pointer",
              }}
            >
              {t("kindAllowances") || "Allowance (Green)"}
            </button>
            <button
              type="button"
              onClick={() => setKind("deduction")}
              style={{
                flex: 1,
                padding: "8px",
                borderRadius: "8px",
                border: kind === "deduction" ? "1.5px solid #9C0006" : `1px solid ${currentTheme.colors.border}`,
                background: kind === "deduction" ? (theme === "dark" ? "rgba(156,0,6,0.25)" : "#FFC7CE") : "transparent",
                color: kind === "deduction" ? (theme === "dark" ? "#fca5a5" : "#9C0006") : currentTheme.colors.textSecondary,
                fontWeight: kind === "deduction" ? 700 : 400,
                fontSize: "0.82rem",
                cursor: "pointer",
              }}
            >
              {t("kindDeductions") || "Deduction (Red)"}
            </button>
          </div>
        </div>

        {/* Variants checkboxes */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <label style={{ fontSize: "0.8rem", fontWeight: 600, color: currentTheme.colors.textSecondary }}>
            {t("showVariants") || "Variants to include"}
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {[
              { key: "net", label: t("variantNet") || "Net" },
              { key: "gross", label: t("variantGross") || "Gross" },
              { key: "days_net", label: t("variantDaysNet") || "Days (Net)" },
              { key: "days_gross", label: t("variantDaysGross") || "Days (Gross)" },
            ].map((v) => {
              const checked = selectedVariants.includes(v.key);
              return (
                <label
                  key={v.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px",
                    borderRadius: "6px",
                    border: checked ? `1px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.borderLight}`,
                    background: checked ? `${currentTheme.colors.primary}12` : "transparent",
                    cursor: "pointer",
                    fontSize: "0.78rem",
                    color: currentTheme.colors.textPrimary,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggleVariant(v.key)}
                  />
                  <span>{v.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Live Preview & Importer Recognition Box (§8 Flow E) */}
        <div
          style={{
            marginTop: "6px",
            padding: "14px",
            borderRadius: "10px",
            background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            border: `1px solid ${currentTheme.colors.borderLight}`,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div style={{ fontSize: "0.76rem", fontWeight: 700, textTransform: "uppercase", color: currentTheme.colors.textMuted }}>
            {t("customColumnPreview") || "Headers that will be created"}
          </div>

          {validation.valid ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {validation.headers.map((h) => (
                <div
                  key={h.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.78rem",
                    color: currentTheme.colors.textPrimary,
                    fontFamily: "monospace",
                    background: theme === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.7)",
                    padding: "4px 8px",
                    borderRadius: "4px",
                  }}
                >
                  <span>{h.header}</span>
                  <span style={{ color: currentTheme.colors.textMuted }} dir="ltr">{h.key}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: "0.76rem", color: currentTheme.colors.textMuted, fontStyle: "italic" }}>
              {t("customColumnHeadersHint")}
            </div>
          )}

          {validation.valid && (
            <div
              style={{
                borderTop: `1px solid ${currentTheme.colors.borderLight}`,
                paddingTop: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                fontSize: "0.76rem",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", color: currentTheme.colors.textSecondary }}>
                <span>{t("customColumnStoredAs") || "Stored as type"}:</span>
                <strong style={{ fontFamily: "monospace", color: currentTheme.colors.primary }}>
                  {validation.derivedType}
                </strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", color: currentTheme.colors.textSecondary }}>
                <span>{t("customColumnRecognised") || "Recognised by the importer"}:</span>
                <span style={{ color: currentTheme.colors.success, fontWeight: 700 }}>
                  ✅ {t("yes")}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Validation Error Message */}
        {!validation.valid && label.trim() && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 12px",
              borderRadius: "8px",
              background: `${currentTheme.colors.danger}15`,
              color: currentTheme.colors.danger,
              fontSize: "0.78rem",
            }}
          >
            <AlertCircle size={15} />
            <span>{validation.error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ marginTop: "auto", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 14px",
              borderRadius: "8px",
              border: `1px solid ${currentTheme.colors.border}`,
              background: "transparent",
              color: currentTheme.colors.textSecondary,
              fontSize: "0.82rem",
              cursor: "pointer",
            }}
          >
            {t("cancel") || "Cancel"}
          </button>
          <button
            type="submit"
            disabled={!validation.valid}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              background: currentTheme.colors.primary,
              color: "#fff",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: validation.valid ? "pointer" : "not-allowed",
              opacity: validation.valid ? 1 : 0.5,
            }}
          >
            {t("addColumn") || "Add column"}
          </button>
        </div>
      </form>
    </div>
  );
};
